<script setup>
import {ref, computed, defineAsyncComponent, getCurrentInstance, watch} from 'vue'
import { useRoute, useRouter } from 'vue-router'

  const cashModalTextHTML = `
    <h2>Получение наличных в кассе РНКО «СТАТЕРА» (ООО)</h2>
    <p>Для получения наличных денежных средств необходимо лично обратиться в отделение РНКО «СТАТЕРА» (ООО) по адресу:</p>
    <address>
      384900, Республика Абхазия,<br>
      г. Сухум, ул. Лакоба, д. 34
    </address>
    <p>При обращении в кассу необходимо предоставить:</p>
    <ul>
      <li>Паспорт на имя получателя.</li>
      <li>Информацию для оформления заявки:
        <ul>
          <li>Фамилия, имя, отчество получателя;</li>
          <li>Сумма к получению;</li>
          <li>Номер квитанции.</li>
        </ul>
      </li>
    </ul>
    <p>Сотрудник кассы оформит заявку на основании предоставленных данных. После проверки документов будет осуществлена выдача наличных денежных средств.</p>
    <p><strong>Важно:</strong> паспортные данные должны совпадать с информацией, указанной в электронном кошельке.</p>
  `;

  const BaseButton = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/buttons/BaseButton.vue" */ '@/components/base/buttons/BaseButton.vue')
  )
  const BackBtn = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/buttons/BackBtn.vue" */ '@/components/base/buttons/BackBtn.vue')
  )
  const TextModal = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/TextModal.vue" */ '@/components/TextModal.vue')
  )
  const PayModal = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/PayModal.vue" */ '@/components/PayModal.vue')
  )
  const WalletList = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/WalletList.vue" */ '@/components/WalletList.vue')
  )
  const NetworkSelect = defineAsyncComponent(() =>
      import(/* webpackChunkName: "BaseSelect" */ '@/components/base/selects/NetworkSelect.vue')
  )
  const BaseInput = defineAsyncComponent(() =>
      import(/* webpackChunkName: "BaseInput" */ '@/components/base/inputs/BaseInput.vue')
  )
  const CardInput = defineAsyncComponent(() =>
      import(/* webpackChunkName: "CardInput" */ '@/components/base/inputs/CardInput.vue')
  )

  import { useStore } from 'vuex'
  const vuexStore = useStore()

  import { settings } from "../../appType/settings.js";
  const envName = settings.env.VUE_APP_ENV_NAME

  const instance = getCurrentInstance();
  const $http = instance.proxy.$http
  const text = instance.proxy.text
  const route = useRoute()
  const router = useRouter()

  const modalStage = ref('enterPin')
  const amount = ref('')
  const card = ref('')
  const address = ref('')
  const network = ref('')
  const errorMessage = ref('')
  const modalActive = ref(false)
  const withdrawalDisabled = ref(false)
  const sbpModalActive = ref(false)
  const cashModalActive = ref(false)
  const cardModalActive = ref(false)
  const cashModalText = cashModalTextHTML

  const authInfo = computed(() => {
    return vuexStore.state.authInfo || {}
  })
  const activeWallet = computed(() => {
    return vuexStore.state.currentWallet
  })
  const authToken = computed(() => {
    return vuexStore.state.authToken
  })
  const walletList = computed(() => {
    return vuexStore.state.walletList || []
  })
  const routeAccountId = computed(() => {
    return route.query.account_id ? String(route.query.account_id) : ''
  })
  const formattedCard = computed({
    get() {
      return card.value.replace(/\s+/g, '').replace(/(\d{4})/g, '$1 ').trim();
    },
    set(v) {
      card.value = v.replace(/\s/g, '');
    }
  })

  function formatCard() {
    card.value = card.value.replace(/\D/g, '').substring(0, 16);
  }
  function isValidCryptoAddress(addressValue, networkId) {
    const trimmedAddress = addressValue.trim()

    if (networkId === 'ethereum') {
      return /^0x[a-fA-F0-9]{40}$/.test(trimmedAddress)
    }

    if (networkId === 'tron') {
      return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(trimmedAddress)
    }

    return true
  }
  function showModal () {
    if (isCryptoSelected.value && !isCryptoAddressValid.value) {
      return
    }

    modalActive.value = true;
    modalStage.value = 'enterPin';
  }
  async function doWithdrawal(pinCode) {
    modalStage.value = 'waiting';

    const withdrawalData = {
      "amount": amount.value,
      "currency_code": activeWallet.value?.currency_code,
      "pin": pinCode
    }

    if (!isCryptoSelected.value) {
      withdrawalData.card = card.value
    } else {
      withdrawalData.address = address.value.trim()
      withdrawalData.network = network.value.id
    }

    const { data } = await $http.post(
        '/v2/wallet/withdrawal',
        withdrawalData,
        {
          "headers": {
            "Authorization": "Token " + authToken.value
          },
          "validateStatus": status => (status >= 200 && status < 500)
        },
    );

    if (data.status === "error") {
      modalStage.value = 'failed';
      errorMessage.value = data.message
    } else {
      modalActive.value = false
      if (data.wallet_transaction_id) {
        router.replace({
          name: 'WalletWithdrawalTransaction',
          params: { id: data.wallet_transaction_id },
        })
      } else {
        modalStage.value = 'success'
        modalActive.value = true
      }
    }
  }

  // TODO сделать универсальное DISABLED computed свойство для кнопок
  const isWithdrawalAllowed = computed(() => {
    return authInfo.value['is_withdrawal_allowed']
  })
  const isCryptoSelected = computed(() => {
    return activeWallet.value?.currency_code === 'USDC' || activeWallet.value?.currency_code === 'USDT'
  })
  const isCryptoAddressValid = computed(() => {
    if (!isCryptoSelected.value || !network.value || !address.value) {
      return false
    }

    return isValidCryptoAddress(address.value, network.value.id)
  })
  const cryptoAddressError = computed(() => {
    if (!isCryptoSelected.value || !network.value || !address.value) {
      return ''
    }

    return isCryptoAddressValid.value ? '' : text('wallet_withdrawal.address_invalid')
  })
  const withdrawalButtonDisabled = computed(() => {
    if (!isWithdrawalAllowed.value || !amount.value) {
      return true
    }

    if (isCryptoSelected.value) {
      return !network.value || !address.value || !isCryptoAddressValid.value
    }

    return !card.value
  })

  watch(activeWallet, () => {
    amount.value = ''
    card.value = ''
    network.value = ''
    address.value = ''
  })

  watch([routeAccountId, walletList], ([accountId]) => {
    if (route.name !== 'WalletWithdrawal' || !accountId) {
      return
    }

    const account = walletList.value.find(({ id }) => id === accountId)
    if (account && account.id !== activeWallet.value?.id) {
      vuexStore.commit('updateCurrentWallet', account)
    }
  }, { immediate: true })

  function closeModal () {
    modalActive.value = false;
  }

</script>

<template>
  <div class="wallet_withdrawal">
    <div class="wallet_withdrawal__header">
      <div class="title">
        <div class="back-btn__wrapper">
          <BackBtn @click="$router.back()"/>
        </div>
        {{ text('wallet_withdrawal.select_account') }}
      </div>
      <WalletList style="margin-bottom: 32px" />
      <BaseInput
        v-model="amount"
        :label="text('wallet_withdrawal.amount')"
        :placeholder="text('wallet_withdrawal.amount')"
        number
      />
    </div>
    <div v-if="envName === 'stater'">
      <div class="actions-block">
        <BaseButton
          prefix
          grayscale-accent
          :disabled="!isWithdrawalAllowed || !amount"
          @click="sbpModalActive = true"
        >
          <template #prefix>
            <img height="24" src="@/assets/images/sbp_logo.svg" alt="sbp" />
          </template>
          <template #label>
            {{ text('wallet_topup.methods.cbp') }}
          </template>
        </BaseButton>
        <BaseButton
          prefix
          grayscale-accent
          :disabled="!isWithdrawalAllowed || !amount"
          @click="cardModalActive = true"
        >
          <template #prefix>
            <img height="24" src="@/assets/images/auth_card.png" alt="auth-card" />
          </template>
          <template #label>
            {{ text('wallet_withdrawal.to-card') }}
          </template>
        </BaseButton>
        <BaseButton
          v-if="envName === 'stater'"
          prefix
          grayscale-accent
          :disabled="!isWithdrawalAllowed || !amount"
          @click="cashModalActive = true"
        >
          <template #prefix>
            <img height="24" src="@/assets/images/cash.svg" alt="cash" />
          </template>
          <template #label>
            {{ text('wallet_withdrawal.cash') }}
          </template>
        </BaseButton>

        <template v-if="sbpModalActive || cardModalActive || cashModalActive">
          <TextModal
            @closeModal="sbpModalActive = false"
            v-if="sbpModalActive"
            title=""
            text="Вывод по СБП временно недоступен"
          />
          <TextModal
            @closeModal="cardModalActive = false"
            v-if="cardModalActive"
            title=""
            text="Вывод на карту временно недоступен"
          />
          <TextModal
            @closeModal="cashModalActive = false"
            v-if="cashModalActive"
            title=""
            :text="cashModalText"
          />
        </template>
      </div>
    </div>

    <div v-else>
      <template v-if="isCryptoSelected">
        <NetworkSelect v-model="network" style="margin-bottom: 24px" />
        <BaseInput
          v-model="address"
          :label="text('wallet_withdrawal.address')"
          :placeholder="text('wallet_withdrawal.address')"
          :style="{ marginBottom: cryptoAddressError ? '8px' : '24px' }"
        />
        <div v-if="cryptoAddressError" class="address-error">
          {{ cryptoAddressError }}
        </div>
      </template>
      <CardInput
        v-else
        v-model="formattedCard"
        style="margin: 0 auto 24px"
      />
      <div class="actions-block">
        <BaseButton
          :disabled="withdrawalButtonDisabled"
          grayscale-accent
          @click="showModal()"
        >
          <template #label>
            {{ text('wallet_withdrawal.withdrawal_btn') }}
          </template>
        </BaseButton>
      </div>
    </div>
    <PayModal
      :stage="modalStage"
      :errorMessage="errorMessage"
      :sucessMessage="text('wallet_withdrawal.success_message')"
      @enterPin="doWithdrawal"
      @closeModal="closeModal()"
      v-if="modalActive"
      :pinCode="pinCode"
    />
  </div>
</template>

<style lang="scss" scoped>
  .wallet_withdrawal {
    padding: 24px;
    position: relative;
    height: 100%;
    min-height: 600px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 16px;

    .title {
      position: relative;

      color: var(--text-color-primary);
      font-family: "Rubik", sans-serif;
      font-size: 21px;
      font-style: normal;
      font-weight: 700;
      text-align: center;
      margin-bottom: 32px;

      .back-btn__wrapper {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .input {
      display: flex;
      align-items: center;
      justify-content: space-between;

      background: #262329;
      margin: 32px auto 20px;
      padding: 16px;
      border-radius: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      span {
        color: var(--text-color-primary);
        font-family: "Rubik", sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 135%;
        white-space: nowrap;
      }

      input {
        width: calc(100% - 70px);
        text-align: right;
        color: var(--text-color-primary);
        font-family: "Rubik", sans-serif;
        font-size: 18px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0.36px;
      }
    }

    ::v-deep(.base-input, .base-select-wrapper .select) {
      background-color: var(--layout-bg)!important;

      &.blur {
        padding-top: 30px!important;
      }
    }

    .address-error {
      margin: -2px 0 18px;
      padding: 0 16px;
      color: #F34B47;
      font-size: 13px;
      font-weight: 500;
      line-height: 18px;
    }

    .actions-block {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      margin-top: 10px;
      .base-button {
        box-sizing: border-box;
        width: 100%;
      }

      &:has(.base-button:nth-of-type(2)) .base-button {
        width: calc(50% - 8px);
      }
    }

    @media (max-width: 920px) {
      padding: 16px;
    }
  }
</style>
